import { useId } from "react";

import Tree, {
  TreeBranch,
  TreeBranchLink,
  TreeItem,
  TreeItemContent,
  TreeItemToggle,
} from "ds/components/Tree";
import Toggle from "ds/components/Toggle";
import Typography from "ds/components/Typography";

import { FilteredResourceTypeGroup } from "./helpers";

type ResourceTypeGroupRowProps = {
  group: FilteredResourceTypeGroup;
  selectedTypes: Set<string>;
  onToggleType: (type: string) => void;
  onToggleGroup: (types: string[], select: boolean) => void;
  isExpanded: boolean;
  onToggleExpanded: () => void;
  isExpandToggleDisabled?: boolean;
};

const ResourceTypeGroupRow = ({
  group,
  selectedTypes,
  onToggleType,
  onToggleGroup,
  isExpanded,
  onToggleExpanded,
  isExpandToggleDisabled,
}: ResourceTypeGroupRowProps) => {
  const groupId = useId();

  const selectedCount = group.types.filter((type) => selectedTypes.has(type)).length;
  const allSelected = selectedCount === group.types.length;
  const someSelected = selectedCount > 0 && !allSelected;

  return (
    <TreeBranch>
      <TreeBranchLink>
        <TreeItem>
          <TreeItemToggle
            onPress={onToggleExpanded}
            isExpanded={isExpanded}
            disabled={isExpandToggleDisabled}
            aria-controls={isExpanded ? groupId : undefined}
            aria-expanded={isExpanded}
            aria-label={group.label}
          />
          <TreeItemContent>
            <Toggle
              variant="checkbox"
              typographyVariant="p-body3"
              isSelected={allSelected}
              isIndeterminate={someSelected}
              onChange={() => onToggleGroup(group.types, !allSelected)}
            >
              {group.label}
            </Toggle>
            <Typography tag="span" variant="p-body4" color="secondary">
              {selectedCount}/{group.types.length}
            </Typography>
          </TreeItemContent>
        </TreeItem>
      </TreeBranchLink>

      {isExpanded && (
        <Tree id={groupId} direction="column" role="group" aria-label={group.label}>
          {group.matchingTypes.map((type) => (
            <TreeBranch key={type}>
              <TreeBranchLink>
                <TreeItem isIndented>
                  <TreeItemContent>
                    <Toggle
                      variant="checkbox"
                      typographyVariant="p-body3"
                      isSelected={selectedTypes.has(type)}
                      onChange={() => onToggleType(type)}
                    >
                      {type}
                    </Toggle>
                  </TreeItemContent>
                </TreeItem>
              </TreeBranchLink>
            </TreeBranch>
          ))}
        </Tree>
      )}
    </TreeBranch>
  );
};

export default ResourceTypeGroupRow;
